/**
 * Chat Screen Container Component
 * Orchestrates ConversationsContainer and PlaceDetailsContainer
 */

import React, { useState, useEffect } from 'react';
import { ChatScreenPresenter } from './ChatScreenPresenter';
import { ConversationsContainer } from './conversations/ConversationsContainer';
import { ChatLocation, ChatMessage } from '@/types/chatTypes';

export interface ChatScreenContainerProps {
  initialQuery?: string | null;
  initialAudio?: string | null;
  initialResponse?: string | null;
  initialMessages?: ChatMessage[] | null;
  initialSessionId?: string | null;
  initialSelectedLocation?: ChatLocation | null;
  onPlaceSelect?: (location: ChatLocation | null) => void;
  onLoadingChange?: (isLoading: boolean) => void;
}

export const ChatScreenContainer: React.FC<ChatScreenContainerProps> = ({
  initialQuery,
  initialAudio,
  initialResponse,
  initialMessages,
  initialSessionId,
  initialSelectedLocation,
  onPlaceSelect,
  onLoadingChange,
}) => {
  // Track selected location for passing to PlaceDetailsContainer
  const [selectedLocation, setSelectedLocation] = useState<ChatLocation | null>(
    initialSelectedLocation || null
  );

  // Track messages and sessionId from ConversationsContainer
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const previousIsLoadingRef = React.useRef<boolean>(false);

  // Update selected location when prop changes
  useEffect(() => {
    console.log(
      '🟦 [ChatScreenContainer] initialSelectedLocation prop changed:',
      {
        title: initialSelectedLocation?.title,
        place: initialSelectedLocation?.place,
        place_name: initialSelectedLocation?.place_name,
        place_id: initialSelectedLocation?.place_id,
        timestamp: new Date().toISOString(),
      }
    );
    setSelectedLocation(initialSelectedLocation || null);
  }, [initialSelectedLocation]);

  // Close place details when a new API call starts (isLoading becomes true)
  useEffect(() => {
    // When loading starts (was false, now true), close place details
    if (!previousIsLoadingRef.current && isLoading && selectedLocation) {
      setSelectedLocation(null);
      onPlaceSelect?.(null);
    }

    // Update ref
    previousIsLoadingRef.current = isLoading;

    // Notify parent of loading state change
    onLoadingChange?.(isLoading);
  }, [isLoading, selectedLocation, onPlaceSelect, onLoadingChange]);

  // Handle place selection from conversations
  const handlePlaceSelect = (location: ChatLocation | null) => {
    console.log('🟦 [ChatScreenContainer] 🖱️ handlePlaceSelect called:', {
      title: location?.title,
      place: location?.place,
      place_name: location?.place_name,
      place_id: location?.place_id,
      timestamp: new Date().toISOString(),
      stackTrace: new Error().stack?.split('\n').slice(0, 5).join('\n'),
    });
    console.log('🟦 [ChatScreenContainer] Setting selectedLocation state');
    setSelectedLocation(location);
    console.log('🟦 [ChatScreenContainer] Calling parent onPlaceSelect');
    onPlaceSelect?.(location);
  };

  // Handle closing place details (called when question is clicked)
  const handleClosePlaceDetails = () => {
    if (selectedLocation) {
      setSelectedLocation(null);
      onPlaceSelect?.(null);
    }
  };

  // Callback to receive messages, sessionId, and isLoading from ConversationsContainer
  const handleConversationsData = (
    newMessages: ChatMessage[],
    newSessionId: string | null,
    newIsLoading: boolean
  ) => {
    setMessages(newMessages);
    setSessionId(newSessionId);
    setIsLoading(newIsLoading);
  };

  return (
    <ChatScreenPresenter
      conversations={
        <ConversationsContainer
          initialQuery={initialQuery}
          initialAudio={initialAudio}
          initialResponse={initialResponse}
          initialMessages={initialMessages}
          initialSessionId={initialSessionId}
          onCardClick={() => {}} // Handled internally
          onPlaceNameClick={() => {}} // Handled internally
          onPlaceSelect={handlePlaceSelect}
          onDataChange={handleConversationsData}
          onClosePlaceDetails={handleClosePlaceDetails}
        />
      }
      rightSide={
        selectedLocation
          ? {
              messages,
              sessionId: sessionId || '',
              initialSelectedLocation: selectedLocation,
              onBack: () => {
                setSelectedLocation(null);
                onPlaceSelect?.(null);
              },
            }
          : null
      }
    />
  );
};
